import React from 'react';

/**
 * LinkifiedText
 *
 * Renders user-entered notes/instructions safely with:
 *  - URLs auto-converted to clickable links
 *  - `word-break` + `overflow-wrap` so long URLs never blow out the container
 *  - Links open in a new tab, truncated label "View link" if the raw URL is
 *    longer than 40 chars (keeps layouts tidy while keeping href valid)
 *
 * Safe by construction: we escape by using text nodes; no dangerouslySetInnerHTML.
 */
const URL_RE = /(https?:\/\/[^\s]+)/gi;

const LinkifiedText = ({ text, className = '', linkLabel = null }) => {
  if (!text) return null;

  const parts = [];
  let last = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: 'text', value: text.slice(last, m.index) });
    parts.push({ kind: 'link', value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ kind: 'text', value: text.slice(last) });

  return (
    <span
      className={`whitespace-pre-wrap ${className}`}
      style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
      data-testid="linkified-text"
    >
      {parts.map((p, i) => {
        if (p.kind === 'text') return <React.Fragment key={i}>{p.value}</React.Fragment>;
        const label = linkLabel || (p.value.length > 40 ? 'View link' : p.value);
        return (
          <a
            key={i}
            href={p.value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-700 hover:text-cyan-800 underline decoration-cyan-300 hover:decoration-cyan-600"
          >
            {label}
          </a>
        );
      })}
    </span>
  );
};

export default LinkifiedText;
